<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="areas"/>
        <pi-area-filters :value="areaFilters" :ignored="ignoredLines" @input="setAreaFilters"/>
        <div class="pi-areas__head">
          <p class="pi-page__lead pi-areas__lead">
            {{ stockLead }}
            <pi-definition-tooltip info-key="areas.stockVsUsage" label="Estoque × uso"/>
          </p>
          <pi-export-screen :screen="screen" :filters="exportFilters"/>
        </div>

        <section class="pi-section" aria-labelledby="pi-areas-cards">
          <div class="pi-section__head">
            <h2 id="pi-areas-cards" class="pi-section__title">Estoque por área</h2>
            <p v-if="view.asOfText" class="pi-section__hint pi-areas__asof">
              <span>Dados de: {{ view.asOfText }}</span>
              <pi-definition-tooltip info-key="areas.asOf" label="Dados de"/>
            </p>
            <pi-export-menu title="Métricas das áreas" :screen="screen" :columns="metricColumns" :rows="view.metricRows" :envelope="cleanEnvelope"
                            :filters="exportFilters" :disabled="exportDisabled"/>
          </div>
          <pi-data-state :loading="request.loading" :error="request.error" :envelope="stateEnvelope" :empty="notStarted"
                         :empty-message="notStartedText" :show-footer="false" @retry="load(true)">
            <div class="pi-areas__grid">
              <pi-area-card v-for="card in view.cards" :key="card.key" :ref="'card-' + card.key" :card="card"
                            :focused="focusArea === card.key" :expanded="expanded[card.key] === true" @toggle="toggle(card.key)"/>
            </div>
          </pi-data-state>
        </section>

        <section v-if="view.coverageRows.length" class="pi-section" aria-labelledby="pi-areas-coverage">
          <div class="pi-section__head">
            <h2 id="pi-areas-coverage" class="pi-section__title">Cobertura por banco</h2>
            <pi-definition-tooltip info-key="areas.coverage" label="Cobertura por banco"/>
            <pi-export-menu title="Cobertura por banco" :screen="screen" :columns="coverageColumns" :rows="view.coverageRows" :envelope="cleanEnvelope"
                            :filters="exportFilters" :disabled="exportDisabled"/>
          </div>
          <p class="pi-muted pi-areas__hint">
            Leitura de cada área em cada banco de clientes no último retrato. Banco sem leitura deixa as empresas dele fora do total e a métrica fica Parcial.
          </p>
          <framed-table>
            <thead>
            <tr>
              <pi-th v-for="column in coverageHeaders" :key="column.key" :column="column"/>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in view.coverageRows" :key="row.key">
              <td>{{ row.shardCode }}</td>
              <td class="is-num">{{ row.companiesText }}</td>
              <td v-for="cell in row.list" :key="cell.areaKey">
                <span class="pi-badge" :class="'pi-badge--' + cell.tone" :title="cell.title || null">{{ cell.label }}</span>
                <span v-if="cell.detail" class="pi-table__sub">{{ cell.detail }}</span>
              </td>
            </tr>
            </tbody>
          </framed-table>
        </section>

        <pi-page-footer :envelopes="[cleanEnvelope]"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import framedTable from "../../components/default/framedTable.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import piTh from "../../components/pi/piTh.vue";
import piAreaFilters from "../../components/pi/piAreaFilters.vue";
import piAreaCard from "../../components/pi/piAreaCard.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { piGet } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportProvide, filterLines } from "../../components/pi/piExport.js";
import { PI_FILTER_DEFAULTS, PI_FILTER_KEY, ignoredFilters, toQueryString } from "../../components/pi/piQuery.js";
import { blankRequest, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import {
  AREAS_NOT_STARTED, AREA_COVERAGE_COLUMNS, AREA_FILTER_FIELDS, AREA_METRIC_COLUMNS, AREA_SCREEN, NOT_STARTED_NOTE_PREFIX, SHARED_NOTE_PREFIXES,
  STOCK_LEAD, areaEnvelope, areaFilterLines, areaFiltersOf, areaParams, areaQueryState, areasView, sharedFilterPatch,
} from "../../components/pi/piAreasModel.js";
import { PI_AREAS } from "../../../../urlConfig";

export default {
  name: "pi-areas",
  components: {
    boxDefault, framedTable, piTabs, piDataState, piPageFooter, piDefinitionTooltip, piExportMenu, piExportScreen, piTh, piAreaFilters, piAreaCard,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    const seeded = areaQueryState(this.$route && this.$route.query);
    return {
      request: blankRequest(),
      journey: seeded.journey,
      focusArea: seeded.area,
      pendingFocus: !!seeded.area,
      expanded: {},
      metricColumns: AREA_METRIC_COLUMNS,
      coverageColumns: AREA_COVERAGE_COLUMNS,
      coverageHeaders: screenColumns(AREA_COVERAGE_COLUMNS),
      notStartedText: AREAS_NOT_STARTED,
      stockLead: STOCK_LEAD,
      screen: AREA_SCREEN,
    };
  },
  computed: {
    areaFilters() {
      return areaFiltersOf(this.filters, this.journey);
    },
    ignoredLines() {
      return ignoredFilters(this.filters, AREA_FILTER_FIELDS);
    },
    params() {
      return areaParams(this.areaFilters);
    },
    apiKey() {
      return toQueryString(this.params);
    },
    view() {
      return areasView(payloadOf(this.request.envelope), { query: this.$route.query, journey: this.journey, filters: this.areaFilters });
    },
    notStarted() {
      return this.request.loaded && this.view.notStarted;
    },
    cleanEnvelope() {
      return areaEnvelope(this.request.envelope);
    },
    stateEnvelope() {
      return this.notStarted ? areaEnvelope(this.request.envelope, SHARED_NOTE_PREFIXES.concat([NOT_STARTED_NOTE_PREFIX])) : this.cleanEnvelope;
    },
    exportDisabled() {
      return !this.request.loaded || !!this.request.error || this.view.notStarted;
    },
    exportFilters() {
      return filterLines({
        filters: this.filters,
        fields: AREA_FILTER_FIELDS,
        extra: areaFilterLines(this.areaFilters).concat([this.view.snapshotLine]),
      });
    },
  },
  watch: {
    apiKey() {
      this.load(false);
    },
    "$route.query"(query) {
      const seeded = areaQueryState(query);
      if (!seeded.present) return;
      this.journey = seeded.journey;
      this.focusArea = seeded.area;
      this.pendingFocus = !!seeded.area;
      if (this.request.loaded && !this.request.loading) this.revealFocus();
    },
  },
  created() {
    this.load(false);
  },
  methods: {
    load(force) {
      const key = this.apiKey;
      if (!shouldLoad(this.request, key, force)) return;
      runRequest(this.request, () => piGet(PI_AREAS, this.params), "carregar as áreas", key).then((applied) => {
        if (applied) this.revealFocus();
      });
    },
    setAreaFilters(next) {
      const patch = sharedFilterPatch(this.filters, next);
      if (Object.keys(patch).length) this.filters = Object.assign({}, this.filters, patch);
      this.journey = next && next.journey ? next.journey : "";
    },
    toggle(key) {
      const next = Object.assign({}, this.expanded);
      next[key] = next[key] !== true;
      this.expanded = next;
    },
    revealFocus() {
      if (!this.pendingFocus || !this.focusArea) return;
      this.pendingFocus = false;
      this.$nextTick(() => {
        const refs = this.$refs["card-" + this.focusArea];
        const card = Array.isArray(refs) ? refs[0] : refs;
        if (card && card.$el && typeof card.$el.focus === "function") card.$el.focus();
      });
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-areas__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px 16px;
  margin: 0 0 12px;
}

.pi-areas__lead {
  flex: 1 1 420px;
  margin: 0;
}

.pi-areas__asof {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pi-areas__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 14px;
}

.pi-areas__hint {
  margin-bottom: 10px;
}

@media (max-width: 767px) {
  .pi-areas__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piAreas.vue